import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getContractor,
  getContractorServices,
  getServices,
  manageContractorServices,
  createContractorContact,
} from "../services/contractorsService";
import s3Service from "../services/s3Service";
import styles from "./ContractorShow.module.css";
import AddServiceModal from "../components/contractors/AddServiceModal";
import AddContactModal from "../components/contractors/AddContactModal";
import ManageContactsModal from "../components/contractors/ManageContactsModal";
import DocumentsSection from "../components/contractors/DocumentsSection";
import PublicLiabilityInsuranceSection from "../components/contractors/PublicLiabilityInsuranceSection";
import MaintenanceTable from "../components/contractors/MaintenanceTable";

const ContractorShow = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [services, setServices] = useState([]);
  const [contractorServices, setContractorServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showManageContactsModal, setShowManageContactsModal] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  // Load contractor data
  useEffect(() => {
    loadContractorData();
  }, [id]);

  const loadContractorData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load contractor details
      const contractorResponse = await getContractor(id);
      if (contractorResponse.success) {
        console.log(`📄 Contractor documents loaded:`, contractorResponse.data.documents?.length || 0);
        if (contractorResponse.data.documents && contractorResponse.data.documents.length > 0) {
          console.log(`📄 Document names:`, contractorResponse.data.documents.map(d => d.name || d.file));
        }
        setContractor(contractorResponse.data);
        // Contacts are included in the contractor data from backend
        if (contractorResponse.data.contacts) {
          setContacts(contractorResponse.data.contacts || []);
        } else {
          setContacts([]);
        }
        
        // Load logo with signed URL if logo exists
        if (contractorResponse.data.logo) {
          try {
            // Logo is stored as S3 key (e.g., "uploads/images/contractors/filename.jpg")
            const signedUrl = await s3Service.getFileUrlFromBackend(contractorResponse.data.logo);
            setLogoUrl(signedUrl);
          } catch (error) {
            console.error("Error loading logo signed URL:", error);
            // Fallback to direct logo value if signed URL fails
            setLogoUrl(contractorResponse.data.logo);
          }
        } else {
          setLogoUrl(null);
        }
      } else {
        throw new Error("Failed to load contractor");
      }

      // Load services
      const servicesResponse = await getServices();
      if (servicesResponse.success) {
        setServices(servicesResponse.data || []);
      }

      // Load contractor services
      const contractorServicesResponse = await getContractorServices(id);
      if (contractorServicesResponse.success) {
        setContractorServices(contractorServicesResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading contractor data:", error);
      setError("Error loading contractor data");
      toast.error("Error loading contractor data");
    } finally {
      setLoading(false);
    }
  };

  // Handle service management
  const handleManageServices = async (selectedServices) => {
    try {
      const response = await manageContractorServices(id, {
        services: selectedServices,
      });
      if (response.success) {
        toast.success("Services updated successfully");
        setShowAddServiceModal(false);
        // Reload contractor data to get updated services
        loadContractorData();
      } else {
        toast.error("Failed to update services");
      }
    } catch (error) {
      console.error("Error managing services:", error);
      toast.error("Error updating services");
    }
  };

  // Handle adding contact
  const handleAddContact = async (contactData) => {
    try {
      setLoading(true);
      const response = await createContractorContact(id, contactData);
      console.log("📋 [ContractorShow] createContractorContact response:", response);
      
      if (response && response.success) {
        toast.success("Contact added successfully");
        setShowAddContactModal(false);
        // Reload contractor data to get updated contacts
        loadContractorData();
        return response; // Return success response
      } else {
        // Return error response so modal can display field-specific errors
        console.log("📋 [ContractorShow] Error response:", response);
        console.log("📋 [ContractorShow] Response errors:", response?.errors);
        console.log("📋 [ContractorShow] Response message:", response?.message);
        
        // Don't show toast here - let modal handle it to avoid duplicate messages
        return response;
      }
    } catch (error) {
      console.error("Error adding contact:", error);
      // Return error object for modal to handle
      return {
        success: false,
        message: error.message || "Error adding contact. Please try again.",
        errors: {}
      };
    } finally {
      setLoading(false);
    }
  };

  // Handle document removal
  const handleDocumentRemoved = (documentId) => {
    // Update the contractor state to remove the deleted document
    setContractor((prev) => ({
      ...prev,
      documents: prev.documents.filter((doc) => doc.id !== documentId),
    }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  // Format address
  const formatAddress = (contractor) => {
    const parts = [];
    if (contractor.address_1) parts.push(contractor.address_1);
    if (contractor.address_2) parts.push(contractor.address_2);
    if (contractor.address_3) parts.push(contractor.address_3);
    return parts.length > 0 ? parts.join("<br>") : "-";
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading contractor details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>Contractor not found</div>
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      style={{ paddingLeft: "160px", paddingRight: "160px" }}
    >
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>
          Dashboard
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/contractors" className={styles.breadcrumbItem}>
          Contractors
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>{contractor.name}</span>
      </div>

      <div className={styles.row}>
        {/* Contractor Overview */}
        <div className={styles.colMd6}>
          <div className={styles.panelTitle}>
            <h3 className={styles.titleStandard}>Contractor overview</h3>
            <Link
              to={`/contractors/${id}/edit`}
              className={styles.btnSecondary}
            >
              <i className={styles.glyphiconCheck}></i> Edit Contractor
            </Link>
          </div>
          <div className={styles.panelGroup}>
            {logoUrl && (
              <div className={styles.companyLogo}>
                <img
                  className={styles.accountAvatar}
                  src={logoUrl}
                  alt="Company Logo"
                  width="105"
                  height="105"
                  onError={(e) => {
                    console.error("Error loading logo image:", e);
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            <dl className={`${styles.greenList} ${styles.greenListWithWidth}`}>
              <div className={styles.fieldRow}>
                <dt>Contractor ID:</dt>
                <dd>{contractor.contractor_id || contractor.id}</dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Contractor name:</dt>
                <dd>{contractor.name}</dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Address:</dt>
                <dd>
                  {contractor.address_1 && (
                    <>
                      {contractor.address_1}<br />
                      {contractor.address_2 && <>{contractor.address_2}<br /></>}
                      {contractor.address_3 && <>{contractor.address_3}</>}
                    </>
                  )}
                  {!contractor.address_1 && "-"}
                </dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Postcode:</dt>
                <dd>{contractor.postcode || "-"}</dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Phone:</dt>
                <dd>{contractor.phone_number || "-"}</dd>
              </div>

              {contractor.phone_number_alternative && (
                <div className={styles.fieldRow}>
                  <dt>Alternative Phone:</dt>
                  <dd>{contractor.phone_number_alternative}</dd>
                </div>
              )}

              <div className={styles.fieldRow}>
                <dt>Email:</dt>
                <dd>
                  <a
                    href={`mailto:${contractor.email}`}
                    className={styles.mailtoInDd}
                  >
                    {contractor.email}
                  </a>
                </dd>
              </div>

              {contractor.email_alternative && (
                <div className={styles.fieldRow}>
                  <dt>Alternative Email:</dt>
                  <dd>
                    <a
                      href={`mailto:${contractor.email_alternative}`}
                      className={styles.mailtoInDd}
                    >
                      {contractor.email_alternative}
                    </a>
                  </dd>
                </div>
              )}

              <div className={styles.fieldRow}>
                <dt>Fax:</dt>
                <dd>-</dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Web:</dt>
                <dd>
                  {contractor.web ? (
                    <a
                      href={
                        contractor.web.startsWith("http")
                          ? contractor.web
                          : `http://${contractor.web}`
                      }
                    >
                      {contractor.web}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>

              <div className={styles.fieldRow}>
                <dt>Review Date:</dt>
                <dd>{formatDate(contractor.date_review)}</dd>
              </div>

              {contractor.notes && (
                <div className={styles.fieldRow}>
                  <dt>Notes:</dt>
                  <dd
                    dangerouslySetInnerHTML={{
                      __html: contractor.notes.replace(/\n/g, "<br>"),
                    }}
                  ></dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Contractor Services */}
        <div className={styles.colMd6}>
          <div className={styles.panelTitle}>
            <h3 className={styles.titleStandard}>Contractor services</h3>
            <button
              className={styles.btnSecondary}
              onClick={() => setShowAddServiceModal(true)}
            >
              <i className={styles.glyphiconPlus}></i> Manage service
            </button>
          </div>
          <div className={styles.panelGroup}>
            <ul className={styles.contractorServiceList}>
              {contractorServices.map((service) => (
                <li key={service.id}>{service.name}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contractor Contacts */}
        <div className={styles.colMd12}>
          <div className={styles.panelTitle}>
            <h3 className={styles.titleStandard}>Contractor contacts</h3>
            <button
              className={styles.btnSecondary}
              onClick={() => setShowAddContactModal(true)}
            >
              <i className={styles.glyphiconPlus}></i> Add contractor contact
            </button>
            <button
              className={styles.manageContactsBtn}
              onClick={() => setShowManageContactsModal(true)}
            >
              <i className={styles.glyphiconEdit}></i>
            </button>
          </div>
          <div className={styles.panelGroup}>
            {contacts && contacts.length > 0 ? (
              contacts.map(
                (contact) => {
                  // Only display contacts that have user data (required by Laravel)
                  if (!contact.user) {
                    return null;
                  }
                  return (
                    <div key={contact.id} className={styles.contactRow}>
                      <div className={styles.colMd4}>
                        <dl
                          className={`${styles.greenList} ${styles.dlContacts}`}
                        >
                          <dt>Name:</dt>
                          <dd>{`${contact.user.first_name} ${contact.user.last_name}`}</dd>
                          <dt>Job title:</dt>
                          <dd>{contact.user.job_title ? contact.user.job_title : <br />}</dd>
                          <dt>Department</dt>
                          <dd>{contact.department || ""}</dd>
                        </dl>
                      </div>
                      <div className={styles.colMd4}>
                        <dl
                          className={`${styles.greenList} ${styles.dlContacts}`}
                        >
                          <dt>Phone number:</dt>
                          <dd>{contact.user.phone_number ? contact.user.phone_number : <br />}</dd>
                          <dt>Mobile number:</dt>
                          <dd>{contact.user.mobile_number ? contact.user.mobile_number : <br />}</dd>
                          <dt>E-mail:</dt>
                          <dd>
                            <a
                              href={`mailto:${contact.user.email}`}
                              className={styles.mailtoInDd}
                            >
                              {contact.user.email}
                            </a>
                          </dd>
                          <dt>Fax:</dt>
                          <dd>{contact.user.fax_number || ""}</dd>
                        </dl>
                      </div>
                      <div className={styles.colMd4}>
                        {contact.reports_to && (
                          <dl
                            className={`${styles.greenList} ${styles.greenListSmaller}`}
                          >
                            <dt>Reports to:</dt>
                            <dd>{contact.reports_to}</dd>
                          </dl>
                        )}
                      </div>
                    </div>
                  );
                }
              )
            ) : (
              <p>No contacts found</p>
            )}
          </div>
        </div>

        {/* Documents Section */}
        <DocumentsSection
          contractor={contractor}
          onDocumentRemoved={handleDocumentRemoved}
        />

        {/* Public Liability Insurance Section */}
        <PublicLiabilityInsuranceSection contractor={contractor} />

        {/* Maintenance Section */}
        <div className={styles.colMd12}>
          <div className={styles.panelTitle}>
            <h3>Maintenance</h3>
          </div>
          <div className={styles.panelGroup}>
            <MaintenanceTable contractorId={id} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddServiceModal && (
        <AddServiceModal
          services={services}
          contractorServices={contractorServices}
          onSave={handleManageServices}
          onClose={() => setShowAddServiceModal(false)}
        />
      )}

      {showAddContactModal && (
        <AddContactModal
          contractorId={id}
          onSave={handleAddContact}
          onClose={() => setShowAddContactModal(false)}
        />
      )}

      {showManageContactsModal && (
        <ManageContactsModal
          contacts={contacts}
          onClose={() => setShowManageContactsModal(false)}
          onContactDeleted={(contactId) => {
            // Remove deleted contact from local state
            setContacts(prev => prev.filter(contact => contact.id !== contactId));
            // Reload contractor data to ensure consistency
            loadContractorData();
          }}
        />
      )}
    </div>
  );
};

export default ContractorShow;
